import { useState } from 'react';
import { Lock } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { pagesApi } from '../../api/pagesApi';
import { Button } from '../../components/ui/Button';
import { RichTextEditor } from '../../components/admin/RichTextEditor';

const SLUGS_NON_SUPPRIMABLES = ['mentions-legales', 'cgu', 'confidentialite', 'cookies', 'a-propos'];
const SLUGS_FOOTER_UNIQUEMENT = ['mentions-legales', 'cgu', 'confidentialite', 'cookies'];

const VIDE = {
  titre: '',
  slug: '',
  univers: 'commun',
  statut: 'brouillon',
  contenu: '',
  menu_label: '',
  afficher_menu: false,
  afficher_footer: false,
};

export function PagesManager() {
  const { data: pages, loading, refetch } = useFetch(() => pagesApi.adminList(), []);
  const [edition, setEdition] = useState(null); // null = fermé, {} = création, {...page} = édition
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  function ouvrirCreation() {
    setEdition({ ...VIDE });
    setErrors({});
  }

  function ouvrirEdition(page) {
    setEdition({ ...VIDE, ...page, afficher_menu: !!page.afficher_menu, afficher_footer: !!page.afficher_footer });
    setErrors({});
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      if (edition.id) {
        await pagesApi.update(edition.id, edition);
      } else {
        await pagesApi.create(edition);
      }
      setEdition(null);
      refetch();
    } catch (err) {
      setErrors(err.errors || { _global: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette page ?')) return;
    await pagesApi.remove(id);
    refetch();
  }

  if (loading) return <p>Chargement...</p>;

  const editionEstProtegee = edition && SLUGS_NON_SUPPRIMABLES.includes(edition.slug);
  const editionFooterUniquement = edition && SLUGS_FOOTER_UNIQUEMENT.includes(edition.slug);

  return (
    <section>
      <div className="admin-toolbar">
        <h1>Pages CMS</h1>
        {!edition && <Button onClick={ouvrirCreation}>+ Nouvelle page</Button>}
      </div>

      {edition && (
        <form className="admin-form" onSubmit={handleSubmit}>
          {editionEstProtegee && (
            <p className="admin-form__hint admin-form__hint--protegee">
              <Lock size={14} />
              {editionFooterUniquement
                ? " Page légale obligatoire : elle ne peut pas être supprimée, son URL ne peut pas changer, et elle reste réservée au footer (jamais au menu principal). Le titre et le contenu restent librement modifiables."
                : " Page obligatoire : elle ne peut pas être supprimée et son URL ne peut pas changer. Le titre, le contenu et l'affichage dans le menu/footer restent librement modifiables."}
            </p>
          )}

          <div className="admin-form__field">
            <label>Titre</label>
            <input
              value={edition.titre}
              onChange={(e) => setEdition({ ...edition, titre: e.target.value })}
              required
            />
            {errors.titre && <span className="admin-form__error">{errors.titre}</span>}
          </div>

          <div className="admin-form__field">
            <label>Slug (laisser vide pour génération automatique)</label>
            <input
              value={edition.slug || ''}
              onChange={(e) => setEdition({ ...edition, slug: e.target.value })}
              disabled={editionEstProtegee}
            />
          </div>

          <div className="admin-form__field">
            <label>Univers</label>
            <select value={edition.univers} onChange={(e) => setEdition({ ...edition, univers: e.target.value })}>
              <option value="commun">Commun</option>
              <option value="particulier">Particulier</option>
              <option value="professionnel">Professionnel</option>
            </select>
          </div>

          <div className="admin-form__field">
            <label>Statut</label>
            <select value={edition.statut} onChange={(e) => setEdition({ ...edition, statut: e.target.value })}>
              <option value="brouillon">Brouillon</option>
              <option value="publie">Publié</option>
              <option value="archive">Archivé</option>
            </select>
          </div>

          <div className="admin-form__field">
            <label>Contenu</label>
            <RichTextEditor value={edition.contenu} onChange={(html) => setEdition({ ...edition, contenu: html })} />
            {errors.contenu && <span className="admin-form__error">{errors.contenu}</span>}
          </div>

          <h3 className="admin-form__subtitle">Accès depuis le site</h3>
          <p className="admin-form__hint">
            Une page publiée reste accessible uniquement par son URL directe, sauf si tu l'affiches ici dans
            le menu et/ou le footer du site.
          </p>

          <div className="admin-form__field">
            <label>Libellé affiché (menu / footer)</label>
            <input
              value={edition.menu_label || ''}
              onChange={(e) => setEdition({ ...edition, menu_label: e.target.value })}
              placeholder="Laisser vide pour reprendre le titre"
            />
          </div>

          <div className="admin-form__field admin-form__field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={editionFooterUniquement ? false : edition.afficher_menu}
                disabled={editionFooterUniquement}
                onChange={(e) => setEdition({ ...edition, afficher_menu: e.target.checked })}
              />
              Afficher dans le menu principal
            </label>
          </div>

          <div className="admin-form__field admin-form__field--checkbox">
            <label>
              <input
                type="checkbox"
                checked={editionFooterUniquement ? true : edition.afficher_footer}
                disabled={editionFooterUniquement}
                onChange={(e) => setEdition({ ...edition, afficher_footer: e.target.checked })}
              />
              Afficher dans le footer
            </label>
          </div>

          {errors._global && <p className="admin-form__error">{errors._global}</p>}

          <div className="admin-form__actions">
            <Button type="submit" disabled={submitting}>
              {edition.id ? 'Enregistrer' : 'Créer'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEdition(null)}>
              Annuler
            </Button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Titre</th>
            <th>Univers</th>
            <th>Statut</th>
            <th>Menu</th>
            <th>Footer</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pages?.map((page) => {
            const estProtegee = SLUGS_NON_SUPPRIMABLES.includes(page.slug);

            return (
              <tr key={page.id}>
                <td>
                  {page.titre}
                  {estProtegee && (
                    <span className="badge-racine" title="Page obligatoire, non supprimable">
                      <Lock size={12} /> Protégée
                    </span>
                  )}
                </td>
                <td>{page.univers}</td>
                <td>{page.statut}</td>
                <td>{page.afficher_menu ? 'Oui' : '—'}</td>
                <td>{page.afficher_footer ? 'Oui' : '—'}</td>
                <td className="admin-table__actions">
                  {page.statut === 'publie' && (
                    <a href={`/${page.slug}`} target="_blank" rel="noreferrer">
                      Voir
                    </a>
                  )}
                  <button onClick={() => ouvrirEdition(page)}>Modifier</button>
                  {!estProtegee && <button onClick={() => handleDelete(page.id)}>Supprimer</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
